import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import { toastApiError } from "../../../api/apiError"
import { useAddProductArticle } from "../../../api/productsApi"
import { ArticlePicker } from "../../customer-requests/processing/ArticlePicker"
import { ProductPicker } from "./ProductPicker"

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-[11.5px] font-semibold text-grey5">{children}</label>
  )
}

export interface LinkArticleToProductDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fixed when opened from a product's detail page: the user picks an article. */
  productId?: string
  /** Fixed when opened from an article's detail page: the user picks a product. */
  articleId?: string
  onLinked?: () => void
}

/**
 * Links an EXISTING article to an EXISTING product as a BOM component.
 * Modeled on LinkSubstanceDialog.tsx's shape (outer `Dialog` + inner `Body`
 * component so all local state resets when `DialogContent` unmounts on
 * close) but far smaller: no supplier, no concentration, no exemption.
 */
export function LinkArticleToProductDialog({
  open,
  onOpenChange,
  productId,
  articleId,
  onLinked,
}: LinkArticleToProductDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <LinkArticleToProductDialogBody
          productId={productId}
          articleId={articleId}
          onLinked={onLinked}
          onOpenChange={onOpenChange}
        />
      </DialogContent>
    </Dialog>
  )
}

function LinkArticleToProductDialogBody({
  productId,
  articleId,
  onLinked,
  onOpenChange,
}: {
  productId?: string
  articleId?: string
  onLinked?: () => void
  onOpenChange: (open: boolean) => void
}) {
  const { translate } = useTranslation()
  const { mutate, isPending } = useAddProductArticle()

  // Only populated (relevant) on the side that ISN'T fixed by the caller.
  const [pickedArticleId, setPickedArticleId] = useState<string | null>(null)
  const [articleLabel, setArticleLabel] = useState("")
  const [pickedProductId, setPickedProductId] = useState<string | null>(null)
  const [productLabel, setProductLabel] = useState("")

  const canSubmit =
    !isPending && !!(productId ? pickedArticleId : pickedProductId)

  function handleSubmit() {
    if (!canSubmit) return
    mutate(
      {
        productId: productId ?? pickedProductId!,
        articleId: articleId ?? pickedArticleId!,
      },
      {
        onSuccess: () => {
          onLinked?.()
          onOpenChange(false)
        },
        onError: (err) => toastApiError(err, translate, "MC_MD_SAVE_ERROR"),
      },
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{translate("MC_MD_LINK_ARTICLE_TITLE")}</DialogTitle>
        <DialogDescription>
          {translate("MC_MD_LINK_ARTICLE_DESCRIPTION")}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-3 p-1">
        {productId ? (
          <div className="flex flex-col gap-1.5">
            <FieldLabel>
              {translate("MC_MD_LINK_ARTICLE_PICK_ARTICLE")}
            </FieldLabel>
            <ArticlePicker
              triggerLabel={
                articleLabel || translate("MC_MD_LINK_ARTICLE_PICK_ARTICLE")
              }
              onSelect={(id, num) => {
                setPickedArticleId(id)
                setArticleLabel(num)
              }}
              className="h-9 w-full justify-between px-3 text-[13px]"
            />
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <FieldLabel>
              {translate("MC_MD_LINK_ARTICLE_PICK_PRODUCT")}
            </FieldLabel>
            <ProductPicker
              triggerLabel={
                productLabel || translate("MC_MD_LINK_ARTICLE_PICK_PRODUCT")
              }
              onSelect={(id, label) => {
                setPickedProductId(id)
                setProductLabel(label)
              }}
            />
          </div>
        )}
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          {translate("MC_MD_EDIT_CANCEL")}
        </Button>
        <Button
          className="bg-yellow1 text-black hover:bg-yellow2"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {translate("MC_MD_LINK_ARTICLE_SUBMIT")}
        </Button>
      </DialogFooter>
    </>
  )
}
